import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Header from './Header';

/**
 * CONTACT COMPONENT
 * Focus: High-conversion lead capture with Discovery Call workflow.
 * Colors: Aqua Haze (#F0F4F8), River Bed (#4A5568), Cerulean Blue (#2B6CB0).
 */
const Contact = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Global mouse tracking for the parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="relative overflow-x-hidden bg-[#F0F4F8] text-[#4A5568] antialiased font-sans min-h-screen">
      <Header />

      {/* --- GLOBAL DYNAMIC BACKGROUND (NOW FULL PAGE) --- */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.15]"
             style={{ backgroundImage: `radial-gradient(#2B6CB0 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        {/* Floating Interactive Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                x: mousePos.x * (i + 1) * 40,
                y: mousePos.y * (i + 1) * 40,
                rotate: mousePos.x * 45,
              }}
              transition={{ type: 'spring', stiffness: 40, damping: 20 }}
              className="absolute border border-[#2B6CB0]/20 rounded-2xl"
              style={{
                width: `${(i + 1) * 250}px`,
                height: `${(i + 1) * 250}px`,
                left: `${20 + i * 20}%`,
                top: `${15 + i * 20}%`,
              }}
            />
          ))}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .marquee-text { animation: marquee 40s linear infinite; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

      <div className="relative z-10 pt-20">

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* LEFT COLUMN: DIRECT CONTACT */}
          <div className="space-y-12">
            <div>
              <h1 className="text-[#020617] text-6xl md:text-[120px] font-black leading-[0.85] uppercase tracking-tighter">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2B6CB0] to-[#4A5568]">
                  Ready ?
                </span>
              </h1>
              <p className="text-xl text-[#4A5568] mb-8 font-medium">
                Fill out the form or reach out directly.
              </p>
              <p className="text-lg text-[#4A5568]/80 leading-relaxed max-w-md">
                Whether you have a fully-fleshed idea or just a spark of a project, let's build something authoritative together.
              </p>
            </div>

            {/* CONTACT INFO */}
            <div className="space-y-6">
              {/* WhatsApp / Call */}
<a href="https://wa.me/12487034659" className="flex items-center space-x-4 group w-fit">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#2B6CB0]/10 group-hover:bg-[#2B6CB0] transition-all">
                  <svg className="w-5 h-5 text-[#2B6CB0] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#2B6CB0]">Call or WhatsApp</span>
                  <span className="text-xl font-bold text-[#020617] group-hover:text-[#2B6CB0] transition-colors">+1 248-703-4659</span>
                </div>
              </a>

              {/* Email */}
              <a href="mailto:caffryx@gmail.com" className="flex items-center space-x-4 group w-fit">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#2B6CB0]/10 group-hover:bg-[#2B6CB0] transition-all">
                  <svg className="w-5 h-5 text-[#2B6CB0] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#2B6CB0]">Email Inquiry</span>
                  <span className="text-xl font-bold text-[#020617] group-hover:text-[#2B6CB0] transition-colors">caffryx@gmail.com</span>
                </div>
              </a>
            </div>
          </div>

          {/* RIGHT COLUMN: GOOGLE FORM */}
          <div className="bg-white p-10 md:p-12 rounded-[3rem] shadow-xl shadow-[#2B6CB0]/5 border border-white">
            <h2 className="text-3xl font-black text-[#020617] mb-8 uppercase tracking-tight">Project Details</h2>
            <iframe
              src="https://forms.gle/DJE71riVwdyit46T6"
              width="100%"
              height="600"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              className="rounded-2xl"
            >
              Loading…
            </iframe>
          </div>
        </div>

        {/* PROCESS STEPS */}
        <div className="mt-32 pt-20 border-t border-[#2B6CB0]/10">
          <h2 className="text-4xl font-black text-[#020617] text-center mb-16 uppercase tracking-tighter">What happens next?</h2>
          <div className="grid md:grid-cols-3 gap-12 text-center">
            {[
              { 
                step: "1", 
                title: "Inquiry Received", 
                desc: "We'll review your project details and brief our team within 24 hours." 
              },
              { 
                step: "2", 
                title: "Discovery Call", 
                desc: "Once submitted, we will call you or send a Calendly link for a discovery session—whatever works better for you." 
              },
              { 
                step: "3", 
                title: "Execution Plan", 
                desc: "You'll receive a detailed proposal and technical timeline to kick off development." 
              }
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-[#2B6CB0]/10">
                  <span className="text-2xl font-black text-[#2B6CB0]">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-[#020617] mb-2 uppercase">{item.title}</h3>
                <p className="text-[#4A5568] leading-relaxed max-w-[280px] text-sm font-medium italic">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Contact;